// After a GM boundary save the next GM tool must decide from the SAVED rows (gm-boundary-truth.ts oneAtATime). Those rows reach the page
// through the live apply path: pre-C5 the server's encounter_update_delta (ft:encounter-state-received), post-C5 a base_patch compile
// (ft:entity-base-compiled). The server publishes them BEFORE the save's own relay frame and HTTP answer (process_affected_ids runs first),
// so a settle usually finds them already installed. Each install banks the actor rows' movement_rev; a settle that names the saved actor
// revision resolves once that revision is installed, waits at most WAIT_MS for it, and only then falls back to the whole-encounter refetch
// (a dropped frame, a gap the delta lane did not heal). A settle without an expectation (a refusal / stale save) refetches, as before.
// Owner playtest 2026-10-14: every page refetched + reinstalled the whole encounter after every GM status add.
//
// The bank holds the revision of the LATEST install per actor (an older full install lowers it), never the highest ever seen. An install
// that reaches a revision the relay frame announced FIRST is not trusted: movement-truth lifts a stale row's movement_rev to the confirmed
// revision the relay banked, without the saved sections, so such an install cannot prove anything — that settle refetches.

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;

export const SETTLE_INSTALL_WAIT_MS = 2500;
/** Shared bounded install barrier. Recovery must finish before a caller can use the snapshot again. */
export function waitForSavedInstall(deps: {
  installed: () => boolean;
  recover: () => Promise<void>;
  subscribe?: (check: () => void) => () => void;
  pollMs?: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}): Promise<void> {
  if (deps.installed()) return Promise.resolve();
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  return new Promise<void>((resolve, reject) => {
    let finished = false, elapsed = 0, timer: unknown;
    let unsubscribe = () => {};
    const finish = (recover = false) => {
      if (finished) return;
      finished = true; if (timer !== undefined) clearTimer(timer); unsubscribe();
      if (recover) { try { void deps.recover().then(resolve, reject); } catch (error) { reject(error); } }
      else resolve();
    };
    const check = () => { if (deps.installed()) finish(); };
    const tick = () => {
      if (finished) return;
      check();
      if (finished) return;
      if (elapsed >= SETTLE_INSTALL_WAIT_MS) { finish(true); return; }
      const delay = Math.min(deps.pollMs ?? SETTLE_INSTALL_WAIT_MS, SETTLE_INSTALL_WAIT_MS - elapsed);
      elapsed += delay;
      timer = setTimer(tick, delay);
    };
    unsubscribe = deps.subscribe?.(check) ?? unsubscribe;
    tick();
  });
}
const MAX_ENCOUNTERS = 32;
/** `relay`: called from the committed relay frame, right after movement-truth banked `rev` as confirmed. */
export interface SettleExpect { actorId: string; rev: number; relay?: boolean }
export interface SettleDeps {
  /** The whole-encounter refetch + install (window.ftResyncEncounter). */
  resync: (encounterId: string) => Promise<unknown>;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

/** The movement_rev of each member row an install carried (`ids` limits it to the rows the install patched). */
export function installedRevs(rows: unknown, ids?: readonly string[] | null): Array<[string, number]> {
  const wanted = ids ? new Set(ids.map(upper)) : null;
  const out: Array<[string, number]> = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const ec = record(record(row)?.encounter_creatures);
    const id = upper(scalar(ec?.encounter_creature_id));
    const rev = Number(scalar(ec?.movement_rev));
    if (id && Number.isInteger(rev) && rev >= 0 && (!wanted || wanted.has(id))) out.push([id, rev]);
  }
  return out;
}

export function createGmBoundarySettle(deps: SettleDeps) {
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((handle: unknown) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  const banked = new Map<string, Map<string, number>>();
  const relayAhead = new Map<string, Map<string, number>>();
  const waiters = new Set<{ encounterId: string; actorId: string; rev: number; done: () => void }>();
  const settling = new Map<string, Promise<void>>();
  const refetching = new Map<string, { gen: number; run: Promise<void> }>();
  let generation = 0;
  const reached = (encounterId: string, actorId: string, rev: number): boolean => (banked.get(encounterId)?.get(actorId) ?? -1) >= rev;
  const actorsOf = <V>(map: Map<string, Map<string, V>>, key: string): Map<string, V> => {
    let actors = map.get(key);
    if (!actors) {
      actors = new Map<string, V>(); map.set(key, actors);
      if (map.size > MAX_ENCOUNTERS) map.delete(map.keys().next().value!);
    }
    return actors;
  };

  /** One whole-encounter read at a time per encounter. `after`: join the read in flight only when it STARTED after read `after`
   *  (null: join any; -1 never joins — a forced read starts fresh, as before). Returns the read's generation and completion. */
  function refetch(key: string, after: number | null): { gen: number; run: Promise<void> } {
    const inFlight = refetching.get(key);
    if (inFlight && after !== -1 && (after === null || inFlight.gen > after)) return inFlight;
    relayAhead.delete(key); // the read installs the server's saved rows
    const gen = ++generation;
    const run: Promise<void> = (deps.resync(key) ?? Promise.resolve()).then(() => undefined, () => undefined)
      .finally(() => { if (refetching.get(key)?.run === run) refetching.delete(key); });
    const entry = { gen, run };
    refetching.set(key, entry);
    return entry;
  }
  /** A read that ended below the saved revision may predate the save: one more read that started after it (shared by every
   *  expectation waiting on the same insufficient read), then the next tool runs either way. */
  async function readUntil(key: string, actorId: string, rev: number, first: { gen: number; run: Promise<void> }): Promise<void> {
    await first.run;
    if (!reached(key, actorId, rev)) await refetch(key, first.gen).run;
  }

  /** An install landed: bank its actors' revisions and release any settle it satisfies. `full`: the whole roster was replaced.
   *  `raw`: the revisions were read from the server payload BEFORE movement-truth reconciled it (no lift possible). */
  function bank(encounterId: string, revs: Iterable<[string, number]>, full = false, raw = false): void {
    const key = upper(encounterId);
    if (!key) return;
    const ahead = raw ? undefined : relayAhead.get(key);
    const actors = full ? new Map<string, number>() : actorsOf(banked, key);
    for (const [id, rev] of revs) {
      if (ahead && (ahead.get(id) ?? -1) >= rev && (banked.get(key)?.get(id) ?? -1) < rev) continue; // possibly lifted: no proof
      actors.set(id, rev);
    }
    if (full) { banked.delete(key); banked.set(key, actors); if (banked.size > MAX_ENCOUNTERS) banked.delete(banked.keys().next().value!); }
    for (const waiter of [...waiters]) if (waiter.encounterId === key && reached(key, waiter.actorId, waiter.rev)) waiter.done();
  }

  function waitForInstall(key: string, expect: SettleExpect): Promise<void> {
    const actorId = upper(expect.actorId);
    if (reached(key, actorId, expect.rev)) return Promise.resolve();
    if (expect.relay) {
      const ahead = actorsOf(relayAhead, key);
      ahead.set(actorId, Math.max(ahead.get(actorId) ?? -1, expect.rev));
    }
    return waitForSavedInstall({
      installed: () => reached(key, actorId, expect.rev),
      recover: () => readUntil(key, actorId, expect.rev, refetch(key, null)),
      subscribe: check => {
        const waiter = { encounterId: key, actorId, rev: expect.rev, done: check };
        waiters.add(waiter);
        return () => { waiters.delete(waiter); };
      }, setTimer, clearTimer,
    });
  }

  /** The saved state is (or will be) installed; the returned promise resolves once it is, or after the refetch fallback. */
  function settle(encounterId: string, expect?: SettleExpect | null): Promise<void> {
    const key = upper(encounterId);
    const exact = !!expect && Number.isInteger(expect.rev) && expect.rev >= 0 && !!upper(expect.actorId) && upper(expect.actorId) !== key;
    const startedAt = generation;
    const mine = exact ? waitForInstall(key, expect!) : refetch(key, -1).run;
    // A settle already in flight (a forced read after a stale save) still gates the next tool. When it ends, an exact expectation
    // is checked again: an older read finishing can install rows below the saved revision (one more read, started after ours began).
    const previous = settling.get(key);
    const gate = previous ? Promise.all([previous, mine]).then(() => undefined) : mine;
    const settled: Promise<void> = gate.then(() => {
      if (!exact || !previous || reached(key, upper(expect!.actorId), expect!.rev)) return undefined; // without a previous gate, `mine` already read
      return refetch(key, startedAt).run;
    }).finally(() => { if (settling.get(key) === settled) settling.delete(key); });
    settling.set(key, settled);
    return settled;
  }

  return { bank, settle, pending: (encounterId: string) => settling.get(upper(encounterId)) ?? null };
}
